import { Link } from 'react-router-dom'
import { Clock, ArrowRight } from 'lucide-react'
import { Section } from '@/components/layout/Section'
import { Container } from '@/components/layout/Container'
import { CardSlider } from '@/components/ui/CardSlider'
import { Button } from '@/components/ui/button'
import { AnimateIn } from '@/components/motion/AnimateIn'
import type { BlogPost } from '@/types/home'
import { OptimizedImage } from '@/components/ui/OptimizedImage'

export function LatestBlogs({ blogs }: { blogs: BlogPost[] }) {
  if (!blogs.length) return null

  return (
    <div className="bg-page">
      <Container>
        <AnimateIn>
          <Section
            title="Latest from Our Blog"
            subtitle="Market insights, guides & tips"
            action={
              <Button asChild variant="ghost" size="sm" className="hidden sm:flex">
                <Link to="/blog">
                  All Articles <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            }
          >
          <CardSlider size="blog" navVariant="side" hideScrollbar>
            {blogs.map((blog) => (
              <Link
                key={blog.id}
                to={`/blog/${blog.slug}`}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-neutral-100 bg-white shadow-[0_2px_12px_rgba(15,23,42,0.04)] transition-shadow hover:shadow-[0_4px_20px_rgba(15,93,154,0.1)]"
              >
                <div className="relative aspect-16/10 overflow-hidden bg-neutral-100">
                  {blog.image ? (
                    <OptimizedImage
                      src={blog.image}
                      alt={blog.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-linear-to-br from-primary-50 to-neutral-100 text-xs font-semibold uppercase tracking-wide text-primary-400">
                      QBC Blog
                    </div>
                  )}
                  <span className="absolute left-3 top-3 rounded-full bg-white/95 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-700 shadow-sm">
                    {blog.category}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="font-display text-sm font-bold leading-snug text-neutral-900 line-clamp-2 group-hover:text-primary-600">
                    {blog.title}
                  </h3>
                  <p className="mt-2 flex-1 text-xs leading-relaxed text-neutral-500 line-clamp-2">
                    {blog.excerpt}
                  </p>
                  <div className="mt-3 flex items-center gap-1 text-[11px] font-medium text-neutral-400">
                    <Clock className="h-3.5 w-3.5" />
                    {blog.readingTime} min read
                  </div>
                </div>
              </Link>
            ))}
          </CardSlider>
        </Section>
        </AnimateIn>
      </Container>
    </div>
  )
}
